// Provider-neutral seat-map utilities. Every function here operates on
// the canonical shapes (SeatBlock, NormalizedDeckSeatMap, FlightOffer,
// Selections); the raw provider-specific parse and grid build lives in
// providers/<name>/seats.js.

import { get_object_field, log } from '@/utilities/commons';
import {
  is_child,
  is_infant,
  traveler_name,
  traveler_type,
} from '@liveflight/utilities';
import { displayedAvailability } from '@liveflight/resources/seat-map';

/**
 * @param {SeatBlock} seat carries a `restrictedFor` map keyed by traveler_type
 * @param {object} traveler
 * @returns {boolean}
 */
export const is_restricted_for_traveler = (seat, traveler) => {
  const { restrictedFor = {} } = seat || {};
  return restrictedFor[traveler_type(traveler)];
};

export const filterHeldPassengers = (passengers) =>
  passengers.filter((passenger) => !is_infant(passenger));

/**
 * Give the priority of seat choice based on traveler
 * First Infant or Holding parents, then Adults and Teens and Children last
 * Children should always be next to an adult so we put them last to avoid confusion in the possible error messages
 * @param {Object} passenger The traveler object
 * @returns {number} Priority of traveler in seat selection, higher the number higher the priority
 */
const travelerTypePriority = (passenger) => {
  if (is_infant(passenger) || passenger.holding) return 2;
  if (is_child(passenger)) return -1;
  return 0;
};

export const sortPassengers = (passengers) =>
  [...passengers].sort(
    (p1, p2) => travelerTypePriority(p2) - travelerTypePriority(p1)
  );

// Prefer the normalized pricingByTraveler map (O(1) lookup, lowercase
// 'available' strings); fall back to the legacy travelerPricing[]
// array for callers still feeding raw shapes.
const isBlockAvailableForTraveler = (block, travelerId) => {
  if (!block) return false;
  const byTraveler = block.pricingByTraveler;
  if (byTraveler && byTraveler[travelerId]) {
    return byTraveler[travelerId].availability === 'available';
  }
  const pricing = (block.travelerPricing || []).find(
    (p) => p.travelerId === travelerId
  );
  return (
    !!pricing &&
    String(pricing.seatAvailabilityStatus).toUpperCase() === 'AVAILABLE'
  );
};

export const isSeatAvailableForAnyPassenger = (seat) => {
  if (!seat) return false;
  const byTraveler = seat.pricingByTraveler;
  if (byTraveler) {
    return Object.values(byTraveler).some(
      (p) => p.availability === 'available'
    );
  }
  const travelerPricing = seat.travelerPricing || [];
  if (!travelerPricing.length) return false;
  return travelerPricing.some(
    (pricing) =>
      String(pricing.seatAvailabilityStatus).toUpperCase() === 'AVAILABLE'
  );
};

export const isSeatAvailableForAllPassengers = (seat) => {
  if (!seat) return false;
  const byTraveler = seat.pricingByTraveler;
  if (byTraveler) {
    const entries = Object.values(byTraveler);
    if (!entries.length) return false;
    return entries.every((p) => p.availability === 'available');
  }
  const travelerPricing = seat.travelerPricing || [];
  if (!travelerPricing.length) return false;
  return travelerPricing.every(
    (pricing) =>
      String(pricing.seatAvailabilityStatus).toUpperCase() === 'AVAILABLE'
  );
};

export const isSeatAvailableForPassenger = (travelerId, seat) =>
  isBlockAvailableForTraveler(seat, travelerId);

/**
 * Per-traveler pricing entry for a seat, or `{}` on miss. The legacy
 * `fallbackToDefault` arg used to silently return the FIRST traveler's
 * pricing on a miss — a billing hazard — so misses now always return
 * `{}`. Callers that want "any pricing" should opt in explicitly.
 * @param {object} seat
 * @param {string} travelerId
 * @returns {object}
 */
export const getTravelerPricing = (
  seat,
  travelerId /* , fallbackToDefault = true */
) => {
  if (!seat) {
    return {};
  }
  const { travelerPricing = [] } = seat;
  const pricing = travelerPricing.find(
    (entry) => entry && entry.travelerId === travelerId
  );
  return pricing || {};
};

export const getSeatPrice = (seat, travelerId, markedUp = true) => {
  const { price: seatPrice } = getTravelerPricing(seat, travelerId, false);
  if (!seatPrice) {
    return 0;
  }

  let grandTotal = seatPrice.total || 0;
  if (markedUp) {
    grandTotal = seatPrice.grandTotal || seatPrice.total;
  }
  return Math.ceil(Number(grandTotal) * 100) / 100;
};

// Walks FareDetail.additionalServices for a `{type:'seat', seatNumber}` entry.
const find_seat_in_fare_detail = (fare_detail) => {
  const { additionalServices = [] } = fare_detail || {};
  if (!Array.isArray(additionalServices)) return null;
  return (
    additionalServices.find((entry) => {
      const { type = '', seatNumber = '' } = entry || {};
      return type === 'seat' && !!seatNumber;
    }) || null
  );
};

export const getSeatsForTraveler = (flightOffer, travelerId) => {
  const seats = [];
  try {
    const pricings = flightOffer.travelerPricings.filter(
      (t) => t.travelerId === travelerId
    );
    for (let i = 0; i < pricings.length; i += 1) {
      const { fareDetailsBySegment } = pricings[i];
      if (fareDetailsBySegment) {
        for (let j = 0; j < fareDetailsBySegment.length; j += 1) {
          const seat = find_seat_in_fare_detail(fareDetailsBySegment[j]);
          if (seat) {
            seats.push({
              segmentId: fareDetailsBySegment[j].segmentId,
              number: seat.seatNumber,
            });
          }
        }
      }
    }
  } catch (e) {
    log('getSeatsForTraveler error');
    log(e);
  }
  return seats;
};

export const getSeatsForSegment = (flightOffer, segmentId) => {
  const seats = [];
  try {
    const pricings = flightOffer.travelerPricings;
    for (let i = 0; i < pricings.length; i += 1) {
      const { travelerId, fareDetailsBySegment = [] } = pricings[i];
      for (let j = 0; j < fareDetailsBySegment.length; j += 1) {
        const fare = fareDetailsBySegment[j];
        if (fare.segmentId !== segmentId) continue;
        const seat = find_seat_in_fare_detail(fare);
        if (seat) {
          seats.push({ travelerId, number: seat.seatNumber });
        }
      }
    }
  } catch (e) {
    log('getSeatsForSegment error');
    log(e);
  }
  return seats;
};

// "Has this fare detail got at least one selected seat?" against the
// canonical additionalServices[type='seat'] entries.
const has_seat_entry = (fare_detail) => {
  const { additionalServices = [] } = fare_detail || {};
  if (!Array.isArray(additionalServices)) return false;
  return additionalServices.some((entry) => {
    const { type = '', seatNumber = '' } = entry || {};
    return type === 'seat' && !!seatNumber;
  });
};

export const travelerHasSelectedSeats = (flightOffer, travelerId) => {
  try {
    const pricings = flightOffer.travelerPricings.filter(
      (t) => t.travelerId === travelerId
    );
    for (let i = 0; i < pricings.length; i += 1) {
      const { fareDetailsBySegment } = pricings[i];
      if (fareDetailsBySegment) {
        for (let j = 0; j < fareDetailsBySegment.length; j += 1) {
          if (has_seat_entry(fareDetailsBySegment[j])) {
            return true;
          }
        }
      }
    }
  } catch (e) {
    log('travelerHasSelectedSeats error');
    log(e);
    return false;
  }
  return false;
};

export const segmentHasSelectedSeats = (flightOffer, segmentId) => {
  try {
    const pricings = flightOffer.travelerPricings;
    for (let i = 0; i < pricings.length; i += 1) {
      const fareDetailsBySegment = (
        pricings[i].fareDetailsBySegment || []
      ).filter((fare) => fare.segmentId === segmentId);
      if (fareDetailsBySegment) {
        for (let j = 0; j < fareDetailsBySegment.length; j += 1) {
          if (has_seat_entry(fareDetailsBySegment[j])) {
            return true;
          }
        }
      }
    }
  } catch (e) {
    log('segmentHasSelectedSeats error');
    log(e);
    return false;
  }
  return false;
};

/**
 * Group every committed seat by segmentId, attaching the traveler's
 * display name from the form-side `traveler_details`. Used by the
 * resume sidebar's per-segment seat list.
 * @param {object} flight_offer
 * @param {Array<object>} traveler_details
 * @returns {object}
 */
export const seats_per_segment = (flight_offer, traveler_details) => {
  try {
    const segments = {};
    const travelerPricings = flight_offer.travelerPricings || [];
    for (let index = 0; index < travelerPricings.length; index += 1) {
      const { travelerId, fareDetailsBySegment = [] } = travelerPricings[index];
      for (
        let fare_index = 0;
        fare_index < fareDetailsBySegment.length;
        fare_index += 1
      ) {
        const { segmentId, additionalServices = [] } =
          fareDetailsBySegment[fare_index];
        const list = Array.isArray(additionalServices)
          ? additionalServices
          : [];
        const seat = list.find((entry) => {
          const { type = '', seatNumber = '' } = entry || {};
          return type === 'seat' && !!seatNumber;
        });
        if (seat) {
          const traveler = traveler_details.find(
            (t) => t.id === travelerId
          ) || { id: travelerId };

          if (!segments[segmentId]) {
            segments[segmentId] = [];
          }
          segments[segmentId].push({
            ...seat,
            traveler_name: traveler_name(traveler),
            travelerId,
          });
        }
      }
    }
    return segments;
  } catch (e) {
    log('seats_per_segment error', e);
    return {};
  }
};

export const getSegmentsFromFlightOffers = (flightOffers) => {
  const segmentsMap = {};
  const allSegments = flightOffers.flatMap((offer) =>
    offer.itineraries.flatMap((itinerary) => itinerary.segments)
  );
  for (let index = 0; index < allSegments.length; index++) {
    const segment = allSegments[index];
    segmentsMap[segment.id] = segment;
  }
  return segmentsMap;
};

export const getDisplayAvailability = (availability) => {
  const key = (availability || '').toLowerCase();
  return displayedAvailability[key] || availability;
};

export const calculate_total_seats_price = (selections) =>
  Object.values(selections).reduce(
    (total, segment_seats) =>
      total +
      Object.entries(segment_seats).reduce(
        (segment_total, [traveler_id, seat]) =>
          segment_total + getSeatPrice(seat, traveler_id),
        0
      ),
    0
  );

export const are_passengers_seated_on_segment = (selections, segment_id) =>
  Object.keys(selections[segment_id] || {}).length > 0;

export const is_everyone_seated = (selections, segment_id, passenger_count) =>
  Object.keys(selections[segment_id] || {}).length >= passenger_count;

/**
 * Does this mapped deck have at least one available seat for every
 * non-held traveler? Consumes the normalized availableSeatsCount map
 * produced by every provider's build_deck_seatmap; used to bake the
 * per-deck `disabled` flag.
 * @param {object} flight_offer
 * @param {object} mapped_deck
 * @returns {boolean}
 */
export const deck_covers_all_passengers = (flight_offer, mapped_deck) => {
  if (!flight_offer || !mapped_deck) return false;
  const ids = (flight_offer.travelerPricings || [])
    .filter((p) => !is_infant(p))
    .map((p) => p.travelerId);
  if (!ids.length) return false;
  const counts = mapped_deck.availableSeatsCount || {};
  return ids.every((id) => (counts[id] || 0) > 0);
};

/**
 * Compose the per-deck `legendHints` list every provider surfaces on
 * its NormalizedDeckSeatMap. Each hint is a string consumers match
 * against a static legend's `hint` field — entries without a matching
 * hint never render. Facilities serialize their code + label so the
 * legend only shows facility types this specific deck reports.
 * @param {object} [params]
 * @returns {Array<string>}
 */
export const build_legend_hints = ({
  hasFreeSeats = false,
  hasChargeableSeats = false,
  hasOccupiedSeats = false,
  hasBlockedSeats = false,
  hasInfantSuitableSeats = false,
  hasPremiumSeats = false,
  hasExitRows = false,
  facilities = [],
  facilityDescriptions = {},
} = {}) => {
  const hints = [];
  if (hasFreeSeats) hints.push('seat:free');
  if (hasChargeableSeats) hints.push('seat:paid');
  if (hasOccupiedSeats) hints.push('seat:occupied');
  if (hasBlockedSeats) hints.push('seat:blocked');
  if (hasInfantSuitableSeats) hints.push('seat:infant');
  if (hasPremiumSeats) hints.push('seat:premium');
  if (hasExitRows) hints.push('exit');
  const seenCodes = {};
  facilities.forEach((facility) => {
    const { code = '', description: facility_description = '' } =
      facility || {};
    if (!code || seenCodes[code]) return;
    seenCodes[code] = true;
    const description =
      facility_description || facilityDescriptions[code] || '';
    hints.push(`facility:${code}:${description}`);
  });
  return hints;
};

/**
 * Pre-mapping check against the normalized availableSeatsCounters shape
 * (`[{ travelerId, value }]` per segment). Distinguishes "field absent"
 * (optimistic — provider didn't wire the counters yet, return true)
 * from "all zero" (real hard-fail — at least one traveler would have
 * nowhere to sit).
 * @param {Array<object>} seat_map_data
 * @returns {boolean}
 */
export const can_all_passengers_select_one_seat = (seat_map_data) => {
  if (!seat_map_data || !seat_map_data.length) return false;
  return seat_map_data.every((segment) => {
    const counters = segment && segment.availableSeatsCounters;
    if (!Array.isArray(counters) || counters.length === 0) {
      return true;
    }
    return counters.every((c) => c && c.value > 0);
  });
};

// ─── Selection pipeline — pure helpers ───────────────────────────────────
// The seat-selection view orchestrates user interaction (tracking,
// modals, logging, Vuex commits) but defers every pure decision to
// these helpers — keeping the behavior deterministic and unit-testable.

/**
 * Forward lookup: SeatSelection for `(segment_id, traveler_id)`, or
 * undefined. Centralizes the `selections[segment_id][traveler_id]`
 * access consumers used to inline three different ways.
 * @returns {object|undefined}
 */
export const get_passenger_seat = (selections, segment_id, traveler_id) => {
  if (!selections || !segment_id || !traveler_id) return undefined;
  const segment_selections = selections[segment_id];
  if (!segment_selections) return undefined;
  return segment_selections[traveler_id];
};

/**
 * Reverse lookup: travelerId already on `seat_number`, or undefined.
 * @param {object} segment_selections
 * @param {string} seat_number
 * @returns {string|undefined}
 */
export const find_passenger_on_seat = (segment_selections, seat_number) => {
  if (!segment_selections || !seat_number) return undefined;
  return Object.keys(segment_selections).find((pid) => {
    const s = segment_selections[pid];
    return s && s.number === seat_number;
  });
};

/**
 * New Selections with the given traveler unassigned from the given
 * segment. Input is not mutated.
 * @returns {object}
 */
export const remove_seat_selection = (
  parsed_seat_selections,
  segment_id,
  traveler_id
) => {
  const segment = parsed_seat_selections[segment_id];
  if (!segment || !(traveler_id in segment)) return parsed_seat_selections;
  const nextSegment = { ...segment };
  delete nextSegment[traveler_id];
  return { ...parsed_seat_selections, [segment_id]: nextSegment };
};

/**
 * New Selections with the given traveler assigned to `seat` on the
 * given segment. Clears any prior assignment for that traveler on that
 * segment first. Input is not mutated.
 * @returns {object}
 */
export const apply_seat_selection = (
  parsed_seat_selections,
  segment_id,
  traveler_id,
  seat
) => {
  const segment = parsed_seat_selections[segment_id] || {};
  return {
    ...parsed_seat_selections,
    [segment_id]: {
      ...segment,
      [traveler_id]: seat,
    },
  };
};

/**
 * Build the row-indexed "seat chains" (runs of adjacent selected seats)
 * that drive the children-must-sit-with-adults rule.
 * @param {Array<object>} seat_list
 * @param {object} travelers_by_age
 * @returns {object}
 */
export const build_segment_seats_chains = (seat_list, travelers_by_age) => {
  const adults = get_object_field(travelers_by_age, 'ADULT.passengers', {});
  const children = get_object_field(travelers_by_age, 'CHILD.passengers', {});

  const next_chains = {};

  for (let i = 0; i < seat_list.length; i += 1) {
    const pId = seat_list[i].passengerId;
    const seat = seat_list[i];

    let chainLeft = seat.left;
    let chainRight = seat.right;
    let minColumn = seat.column;
    let hasAdult = !!adults[pId];
    let hasChild = !!children[pId];

    const mergedChainIndexes = [];
    const rowChains = next_chains[seat.row] || [];
    let newChain = [seat.number];
    for (let j = 0; j < rowChains.length; j += 1) {
      const current = rowChains[j];
      if (current.left && current.left.number === seat.number) {
        mergedChainIndexes.push(j);
        newChain = [...newChain, ...current.chain];
        chainRight = current.right;
        hasAdult = hasAdult || current.hasAdult;
        hasChild = hasChild || current.hasChild;
      }
      if (current.right && current.right.number === seat.number) {
        mergedChainIndexes.push(j);
        newChain = [...current.chain, ...newChain];
        chainLeft = current.left;
        minColumn = current.minColumn;
        hasAdult = hasAdult || current.hasAdult;
        hasChild = hasChild || current.hasChild;
      }
    }
    next_chains[seat.row] = [
      ...rowChains.filter((_, idx) => !mergedChainIndexes.includes(idx)),
      {
        left: chainLeft,
        chain: newChain,
        right: chainRight,
        minColumn,
        hasAdult,
        hasChild,
      },
    ];
    next_chains[seat.row].sort((c1, c2) => c1.minColumn - c2.minColumn);
  }
  return next_chains;
};

/**
 * Evaluate whether placing the given child on the given seat leaves
 * every unaccompanied-child scenario covered by an adult. When valid,
 * `seatChains` is the freshly-rebuilt chain map the caller should
 * commit; when invalid, `seatChains` is omitted.
 * @returns {{valid: boolean, seatChains?: object}}
 */
export const check_seats_valid_for_children = ({
  segment_id,
  passenger_id,
  seat,
  parsed_seat_selections,
  travelers_by_age,
}) => {
  try {
    const segmentSelections =
      (parsed_seat_selections && parsed_seat_selections[segment_id]) || {};
    const selected = segmentSelections[passenger_id] || {};
    const distinctRows = {};
    const {
      ADULT: adults = {
        count: 0,
        passengers: {},
      },
      TEEN: teens = {
        count: 0,
        passengers: {},
      },
      CHILD: children = {
        passengers: {},
      },
    } = travelers_by_age || {};
    const totalAdults = adults.count;
    const totalNonChild = adults.count + teens.count; // Teens can seat alone
    let adultsLeftToSeat = totalAdults;

    const seatList = [
      ...Object.entries(segmentSelections)
        .map(([pid, s]) => ({ passengerId: pid, ...s }))
        .filter((s) => s.number !== selected.number),
      { passengerId: passenger_id, ...seat },
    ];
    seatList.forEach((s) => {
      distinctRows[s.row || 0] = true;
      if (adults.passengers[s.passengerId]) {
        adultsLeftToSeat -= 1;
      }
    });

    // Children can't span more rows than there are adults to sit near them.
    if (Object.keys(distinctRows).length > totalNonChild) {
      return { valid: false };
    }

    // A child on an isolated seat (both sides blocked / missing) is invalid.
    if (children.passengers[passenger_id]) {
      const { left, right } = seat;
      const isLeftSideBlocked = !left || left.isBlocked;
      const isRightSideBlocked = !right || right.isBlocked;
      if (isLeftSideBlocked && isRightSideBlocked) {
        return { valid: false };
      }
    }

    const nextSeatChains = build_segment_seats_chains(
      seatList,
      travelers_by_age
    );
    const kidsChainsWithNoAdults = Object.values(nextSeatChains)
      .flat()
      .filter((chain) => chain.hasChild && !chain.hasAdult).length;
    if (kidsChainsWithNoAdults > adultsLeftToSeat) {
      return { valid: false };
    }

    return { valid: true, seatChains: nextSeatChains };
  } catch (e) {
    log('check_seats_valid_for_children error');
    log(e);
    return { valid: false };
  }
};
